import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { 
  NOTIFICATION_EVENT_TYPES, 
  REMINDER_PRESETS,
  type NotificationPreferences,
  type NotificationRule 
} from "@shared/schema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Bell, 
  Mail, 
  Phone, 
  MessageSquare, 
  Clock, 
  Calendar, 
  Target, 
  Repeat,
  ArrowLeft,
  Save,
  CheckCircle2
} from "lucide-react";

const EVENT_TYPE_LABELS: Record<string, { label: string; icon: typeof Bell }> = {
  [NOTIFICATION_EVENT_TYPES.TASK_DUE]: { label: "Task Due Reminders", icon: CheckCircle2 },
  [NOTIFICATION_EVENT_TYPES.CALENDAR_EVENT]: { label: "Calendar Event Reminders", icon: Calendar },
  [NOTIFICATION_EVENT_TYPES.GOAL_DEADLINE]: { label: "Goal Deadline Reminders", icon: Target },
  [NOTIFICATION_EVENT_TYPES.HABIT_REMINDER]: { label: "Habit Reminders", icon: Repeat },
};

const COMMUNITY_EVENT_TYPES = {
  [NOTIFICATION_EVENT_TYPES.COMMUNITY_MESSAGE]: "Community Messages (1-on-1)",
  [NOTIFICATION_EVENT_TYPES.COMMUNITY_GROUP_MESSAGE]: "Group Messages",
};

const SUMMARY_FREQUENCIES = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

export default function NotificationSettingsPage() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();

  const [mobilePhone, setMobilePhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  
  const [preferences, setPreferences] = useState<Partial<NotificationPreferences>>({
    emailEnabled: true,
    smsEnabled: false,
    inAppEnabled: true,
    dailySummaryEnabled: false,
    dailySummaryFrequency: "daily",
    dailySummaryTime: "09:00",
    dailySummaryIncludeHabits: true,
    dailySummaryIncludeTasks: true,
    dailySummaryIncludeEvents: true,
    dailySummaryIncludeGoals: true,
  });

  const [rules, setRules] = useState<Record<string, Partial<NotificationRule>>>({});

  const { data: fetchedPreferences, isLoading: preferencesLoading } = useQuery<NotificationPreferences>({
    queryKey: ["/api/notifications/preferences"],
  });

  const { data: fetchedRules = [], isLoading: rulesLoading } = useQuery<NotificationRule[]>({
    queryKey: ["/api/notifications/rules"],
  });

  useEffect(() => {
    if (fetchedPreferences) {
      setMobilePhone(fetchedPreferences.mobilePhone || "");
      setPreferences({
        emailEnabled: fetchedPreferences.emailEnabled,
        smsEnabled: fetchedPreferences.smsEnabled,
        inAppEnabled: fetchedPreferences.inAppEnabled,
        dailySummaryEnabled: fetchedPreferences.dailySummaryEnabled,
        dailySummaryFrequency: fetchedPreferences.dailySummaryFrequency,
        dailySummaryTime: fetchedPreferences.dailySummaryTime,
        dailySummaryIncludeHabits: fetchedPreferences.dailySummaryIncludeHabits,
        dailySummaryIncludeTasks: fetchedPreferences.dailySummaryIncludeTasks,
        dailySummaryIncludeEvents: fetchedPreferences.dailySummaryIncludeEvents,
        dailySummaryIncludeGoals: fetchedPreferences.dailySummaryIncludeGoals,
      });
    }
  }, [fetchedPreferences]);

  useEffect(() => {
    if (fetchedRules && fetchedRules.length > 0) {
      const rulesMap: Record<string, Partial<NotificationRule>> = {};
      fetchedRules.forEach((rule) => {
        rulesMap[rule.eventType] = rule;
      });
      setRules(rulesMap);
    } else if (fetchedRules && fetchedRules.length === 0 && Object.keys(rules).length === 0) {
      const defaultRules: Record<string, Partial<NotificationRule>> = {};
      Object.values(NOTIFICATION_EVENT_TYPES).forEach((eventType) => {
        defaultRules[eventType] = {
          eventType,
          enabled: true,
          notifyByEmail: true,
          notifyBySms: false,
          notifyByInApp: true,
          reminderMinutesBefore: 30,
          repeatIfIncomplete: false,
        };
      });
      setRules(defaultRules);
    }
  }, [fetchedRules]);

  const savePreferencesMutation = useMutation({
    mutationFn: async (data: Partial<NotificationPreferences>) => {
      return apiRequest("PUT", "/api/notifications/preferences", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/preferences"] });
      toast({ title: "Preferences saved", description: "Your notification preferences have been updated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to save preferences", variant: "destructive" });
    },
  });

  const saveRulesMutation = useMutation({
    mutationFn: async (data: Partial<NotificationRule>[]) => {
      return apiRequest("POST", "/api/notifications/rules", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/rules"] });
      toast({ title: "Rules saved", description: "Your notification rules have been updated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to save rules", variant: "destructive" });
    },
  });

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true;
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ""));
  };

  const handleSavePhone = () => {
    if (!validatePhone(mobilePhone)) {
      setPhoneError("Please enter a valid phone number");
      return;
    }
    setPhoneError("");
    savePreferencesMutation.mutate({ ...preferences, mobilePhone });
  };

  const handlePreferenceChange = (key: keyof NotificationPreferences, value: any) => {
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);
    savePreferencesMutation.mutate({ ...updated, mobilePhone });
  };

  const handleRuleChange = (eventType: string, key: keyof NotificationRule, value: any) => {
    const updatedRule = { ...rules[eventType], eventType, [key]: value };
    const updatedRules = { ...rules, [eventType]: updatedRule };
    setRules(updatedRules);
    const rulesToSave = Object.values(updatedRules).map(rule => ({
      ...rule,
      eventType: rule.eventType || eventType,
    }));
    saveRulesMutation.mutate(rulesToSave);
  };

  const isLoading = preferencesLoading || rulesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading notification settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/settings")}
            data-testid="button-back"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Bell className="w-6 h-6" />
              Notification Settings
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage how and when you receive notifications
            </p>
          </div>
        </div>
      </div>

      <div className="w-full max-w-full px-4 md:px-6 py-6 space-y-6">
        {/* Contact Information Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Contact Information
            </CardTitle>
            <CardDescription>
              Your contact details for receiving notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={user?.email || ""}
                disabled
                className="bg-muted"
                data-testid="input-email"
              />
              <p className="text-xs text-muted-foreground">
                Email is managed through your profile settings
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobile-phone">Mobile Phone Number</Label>
              <div className="flex gap-2">
                <Input
                  id="mobile-phone"
                  type="tel"
                  placeholder="+1 555 123 4567"
                  value={mobilePhone}
                  onChange={(e) => {
                    setMobilePhone(e.target.value);
                    if (phoneError) setPhoneError("");
                  }}
                  className={phoneError ? "border-destructive" : ""}
                  data-testid="input-mobile-phone"
                />
                <Button 
                  onClick={handleSavePhone}
                  disabled={savePreferencesMutation.isPending}
                  data-testid="button-save-phone"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
              </div>
              {phoneError && (
                <p className="text-xs text-destructive">{phoneError}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Notification Channels Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notification Channels
            </CardTitle>
            <CardDescription>
              Enable or disable global notification channels
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <div>
                  <Label>Email Notifications</Label>
                  <p className="text-xs text-muted-foreground">Receive notifications via email</p>
                </div>
              </div>
              <Switch
                checked={preferences.emailEnabled}
                onCheckedChange={(checked) => handlePreferenceChange("emailEnabled", checked)}
                data-testid="switch-email-enabled"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-muted-foreground" />
                <div>
                  <Label>SMS/Text Notifications</Label>
                  <p className="text-xs text-muted-foreground">Receive notifications via SMS</p>
                </div>
              </div>
              <Switch
                checked={preferences.smsEnabled}
                onCheckedChange={(checked) => handlePreferenceChange("smsEnabled", checked)}
                data-testid="switch-sms-enabled"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-muted-foreground" />
                <div>
                  <Label>In-App Notifications</Label>
                  <p className="text-xs text-muted-foreground">Receive notifications within the app</p>
                </div>
              </div>
              <Switch
                checked={preferences.inAppEnabled}
                onCheckedChange={(checked) => handlePreferenceChange("inAppEnabled", checked)}
                data-testid="switch-in-app-enabled"
              />
            </div>
          </CardContent>
        </Card>

        {/* Event Reminders Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Event Reminders
            </CardTitle>
            <CardDescription>
              Configure notification rules for different event types
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {Object.entries(EVENT_TYPE_LABELS).map(([eventType, { label, icon: Icon }]) => {
                const rule = rules[eventType] || {};
                const isTaskType = eventType === NOTIFICATION_EVENT_TYPES.TASK_DUE;
                
                return (
                  <div key={eventType} className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5 text-primary" />
                      <h4 className="font-medium">{label}</h4>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="flex items-center justify-between sm:flex-col sm:items-start sm:gap-2">
                        <Label className="text-sm">Email</Label>
                        <Switch
                          checked={rule.notifyByEmail ?? true}
                          onCheckedChange={(checked) => handleRuleChange(eventType, "notifyByEmail", checked)}
                          disabled={!preferences.emailEnabled}
                          data-testid={`switch-${eventType}-email`}
                        />
                      </div>
                      <div className="flex items-center justify-between sm:flex-col sm:items-start sm:gap-2">
                        <Label className="text-sm">SMS</Label>
                        <Switch
                          checked={rule.notifyBySms ?? false}
                          onCheckedChange={(checked) => handleRuleChange(eventType, "notifyBySms", checked)}
                          disabled={!preferences.smsEnabled}
                          data-testid={`switch-${eventType}-sms`}
                        />
                      </div>
                      <div className="flex items-center justify-between sm:flex-col sm:items-start sm:gap-2">
                        <Label className="text-sm">In-App</Label>
                        <Switch
                          checked={rule.notifyByInApp ?? true}
                          onCheckedChange={(checked) => handleRuleChange(eventType, "notifyByInApp", checked)}
                          disabled={!preferences.inAppEnabled}
                          data-testid={`switch-${eventType}-in-app`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Reminder Timing</Label>
                        <Select
                          value={String(rule.reminderMinutesBefore ?? 30)}
                          onValueChange={(value) => handleRuleChange(eventType, "reminderMinutesBefore", parseInt(value))}
                        >
                          <SelectTrigger data-testid={`select-${eventType}-timing`}>
                            <SelectValue placeholder="Select timing" />
                          </SelectTrigger>
                          <SelectContent>
                            {REMINDER_PRESETS.map((preset) => (
                              <SelectItem key={preset.value} value={String(preset.value)}>
                                {preset.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {isTaskType && (
                      <div className="flex items-center gap-2 pt-2 border-t">
                        <Checkbox
                          id={`repeat-${eventType}`}
                          checked={rule.repeatIfIncomplete ?? false}
                          onCheckedChange={(checked) => handleRuleChange(eventType, "repeatIfIncomplete", checked)}
                          data-testid={`checkbox-${eventType}-repeat`}
                        />
                        <Label htmlFor={`repeat-${eventType}`} className="text-sm cursor-pointer">
                          Repeat reminder if task is incomplete
                        </Label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Community Notifications Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Community Notifications
            </CardTitle>
            <CardDescription>
              Configure notifications for community messages and group chats
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(COMMUNITY_EVENT_TYPES).map(([eventType, label]) => {
              const rule = rules[eventType] || {};
              const testIdPrefix = eventType === NOTIFICATION_EVENT_TYPES.COMMUNITY_MESSAGE 
                ? "community-1on1" 
                : "community-group";
              
              return (
                <div key={eventType} className="border rounded-lg p-4 space-y-4" data-testid={`section-${testIdPrefix}`}>
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">{label}</h4>
                    <Switch
                      checked={rule.enabled ?? true}
                      onCheckedChange={(checked) => handleRuleChange(eventType, "enabled", checked)}
                      data-testid={`switch-${testIdPrefix}-enabled`}
                    />
                  </div>
                  {(rule.enabled ?? true) && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="flex items-center justify-between sm:flex-col sm:items-start sm:gap-2">
                        <Label className="text-sm">Email</Label>
                        <Switch
                          checked={rule.notifyByEmail ?? true}
                          onCheckedChange={(checked) => handleRuleChange(eventType, "notifyByEmail", checked)}
                          disabled={!preferences.emailEnabled}
                          data-testid={`switch-${testIdPrefix}-email`}
                        />
                      </div>
                      <div className="flex items-center justify-between sm:flex-col sm:items-start sm:gap-2">
                        <Label className="text-sm">In-App</Label>
                        <Switch
                          checked={rule.notifyByInApp ?? true}
                          onCheckedChange={(checked) => handleRuleChange(eventType, "notifyByInApp", checked)}
                          disabled={!preferences.inAppEnabled}
                          data-testid={`switch-${testIdPrefix}-in-app`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Daily Summary Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Daily Summary
            </CardTitle>
            <CardDescription>
              Receive a periodic summary of your activities
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Daily Summary</Label>
                <p className="text-xs text-muted-foreground">
                  Receive a summary of your habits, tasks, and goals
                </p>
              </div>
              <Switch
                checked={preferences.dailySummaryEnabled}
                onCheckedChange={(checked) => handlePreferenceChange("dailySummaryEnabled", checked)}
                data-testid="switch-daily-summary-enabled"
              />
            </div>

            {preferences.dailySummaryEnabled && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select
                      value={preferences.dailySummaryFrequency}
                      onValueChange={(value) => handlePreferenceChange("dailySummaryFrequency", value)}
                    >
                      <SelectTrigger data-testid="select-summary-frequency">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        {SUMMARY_FREQUENCIES.map((freq) => (
                          <SelectItem key={freq.value} value={freq.value}>
                            {freq.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Time</Label>
                    <Input
                      type="time"
                      value={preferences.dailySummaryTime}
                      onChange={(e) => handlePreferenceChange("dailySummaryTime", e.target.value)}
                      data-testid="input-summary-time"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Include in Summary</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="summary-include-habits"
                        checked={preferences.dailySummaryIncludeHabits}
                        onCheckedChange={(checked) => handlePreferenceChange("dailySummaryIncludeHabits", checked)}
                        data-testid="checkbox-summary-include-habits"
                      />
                      <Label htmlFor="summary-include-habits" className="text-sm cursor-pointer">
                        Habit streak summary
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="summary-include-tasks"
                        checked={preferences.dailySummaryIncludeTasks}
                        onCheckedChange={(checked) => handlePreferenceChange("dailySummaryIncludeTasks", checked)}
                        data-testid="checkbox-summary-include-tasks"
                      />
                      <Label htmlFor="summary-include-tasks" className="text-sm cursor-pointer">
                        Outstanding tasks
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="summary-include-events"
                        checked={preferences.dailySummaryIncludeEvents}
                        onCheckedChange={(checked) => handlePreferenceChange("dailySummaryIncludeEvents", checked)}
                        data-testid="checkbox-summary-include-events"
                      />
                      <Label htmlFor="summary-include-events" className="text-sm cursor-pointer">
                        Upcoming events
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="summary-include-goals"
                        checked={preferences.dailySummaryIncludeGoals}
                        onCheckedChange={(checked) => handlePreferenceChange("dailySummaryIncludeGoals", checked)}
                        data-testid="checkbox-summary-include-goals"
                      />
                      <Label htmlFor="summary-include-goals" className="text-sm cursor-pointer">
                        Goal progress
                      </Label>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
